// Sound effect manager with mute toggle
import { debugLog } from './debug';

const MUTE_STORAGE_KEY = 'advent_muted';

const SOUND_LOG_LEVEL = parseInt(process.env.REACT_APP_SOUND_LOG_LEVEL || '2', 10);

// Sound file paths
const SOUNDS = {
  clickOpen: '/sounds/click-open.wav',
  clickClose: '/sounds/click-close.wav',
  select: '/sounds/select.wav',
  confetti: '/sounds/confetti.wav',
  badge: '/sounds/badge.wav',
  locked: '/sounds/locked.wav',
  infoWindowOpen: '/sounds/info-window-open.wav',
  unlockSpecial: '/sounds/unlock-special.wav',
  unlockRegular: '/sounds/unlock-regular.wav',
};

// Preloaded audio elements
const audioCache: Record<string, HTMLAudioElement> = {};

// Initialize audio cache
function initAudio() {
  Object.entries(SOUNDS).forEach(([key, path]) => {
    const audio = new Audio(path);
    audio.preload = 'auto';
    audio.load(); // Explicitly load the audio

    // Log when audio is loaded
    audio.addEventListener('canplaythrough', () => {
      debugLog(SOUND_LOG_LEVEL, `[Sound] Audio loaded: ${key}`);
    }, { once: true });

    // Log errors
    audio.addEventListener('error', (e) => {
      debugLog(SOUND_LOG_LEVEL, `[Sound] Failed to load audio ${key} from ${path}:`, audio.error);
    }, { once: true });

    audioCache[key] = audio;
  });
}

// Initialize on module load
initAudio();

// Get mute state from localStorage
export function isMuted(): boolean {
  const stored = localStorage.getItem(MUTE_STORAGE_KEY);
  return stored === 'true';
}

// Set mute state and save to localStorage
export function setMuted(muted: boolean): void {
  localStorage.setItem(MUTE_STORAGE_KEY, muted.toString());
}

// Play a sound if not muted
function playSound(soundKey: keyof typeof SOUNDS): void {
  debugLog(SOUND_LOG_LEVEL, `[Sound] Playing sound: ${soundKey}`);
  if (isMuted()) {
    debugLog(SOUND_LOG_LEVEL, `[Sound] Sound muted: ${soundKey}`);
    return;
  }

  const audio = audioCache[soundKey];
  if (!audio) {
    debugLog(SOUND_LOG_LEVEL, `[Sound] Audio not found in cache: ${soundKey}`);
    return;
  }

  // Check if audio is loaded enough to play
  if (audio.readyState < 2) { // HAVE_CURRENT_DATA
    debugLog(SOUND_LOG_LEVEL, `[Sound] Audio not ready yet: ${soundKey}, readyState: ${audio.readyState}`);
    // Try to play anyway - browser will handle it
  }

  // Reset and play the audio element directly
  // This is more reliable than cloning for rapid playback
  audio.currentTime = 0;
  audio.volume = 0.5;

  const playPromise = audio.play();

  if (playPromise !== undefined) {
    playPromise
      .then(() => {
        debugLog(SOUND_LOG_LEVEL, `[Sound] Successfully playing sound: ${soundKey}`);
      })
      .catch(err => {
        debugLog(SOUND_LOG_LEVEL, `[Sound] Sound play failed for ${soundKey}:`, err.message || err);
      });
  }
}

// Exported sound functions
export const sound = {
  clickOpen: () => playSound('clickOpen'),
  clickClose: () => playSound('clickClose'),
  select: () => playSound('select'),
  confetti: () => playSound('confetti'),
  badge: () => playSound('badge'),
  locked: () => playSound('locked'),
  infoWindowOpen: () => playSound('infoWindowOpen'),
  unlockSpecial: () => playSound('unlockSpecial'),
  unlockRegular: () => playSound('unlockRegular'),
};
